import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Calendar, MapPin, ExternalLink, Image } from 'lucide-react';
import FileUploader from './FileUploader';
import { getImageUrl } from '@/utils/helpers';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_TYPES = [
  { value: 'exhibition', label: '艺术展 / Art Exhibition' },
  { value: 'film', label: '电影 / Film' },
  { value: 'theater', label: '戏剧 / Theater' },
  { value: 'other', label: '其他活动 / Other' },
];

const AdminPosters = ({ token }) => {
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPoster, setEditingPoster] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    event_type: 'exhibition',
    start_time: '',
    end_time: '',
    event_location: '',
    link_url: '',
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchPosters();
  }, []);

  const fetchPosters = async () => {
    try {
      const res = await axios.get(`${API}/posters/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosters(res.data);
    } catch (err) {
      console.error('Failed to fetch posters:', err);
      toast.error('获取海报列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image_url) {
      toast.error('请填写标题和上传图片');
      return;
    }

    try {
      const payload = {
        ...formData,
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
        end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null,
      };

      if (editingPoster) {
        await axios.put(`${API}/posters/${editingPoster.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('海报更新成功');
      } else {
        await axios.post(`${API}/posters`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('海报创建成功');
      }

      setDialogOpen(false);
      resetForm();
      fetchPosters();
    } catch (err) {
      console.error('Failed to save poster:', err);
      toast.error(err.response?.data?.detail || '保存失败');
    }
  };

  const handleDelete = async (posterId) => {
    if (!window.confirm('确定要删除这个海报吗？')) return;

    try {
      await axios.delete(`${API}/posters/${posterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('海报已删除');
      fetchPosters();
    } catch (err) {
      toast.error('删除失败');
    }
  };

  const handleEdit = (poster) => {
    setEditingPoster(poster);
    // 格式化日期时间为本地时间字符串 (YYYY-MM-DDTHH:MM)
    const formatDateTimeLocal = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60 * 1000);
      return localDate.toISOString().slice(0, 16);
    };
    
    setFormData({
      title: poster.title || '',
      subtitle: poster.subtitle || '',
      image_url: poster.image_url || '',
      event_type: poster.event_type || 'exhibition',
      start_time: formatDateTimeLocal(poster.start_time),
      end_time: formatDateTimeLocal(poster.end_time),
      event_location: poster.event_location || '',
      link_url: poster.link_url || '',
      is_active: poster.is_active !== false,
      sort_order: poster.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPoster(null);
    setFormData({
      title: '',
      subtitle: '',
      image_url: '',
      event_type: 'exhibition',
      start_time: '',
      end_time: '',
      event_location: '',
      link_url: '',
      is_active: true,
      sort_order: 0,
    });
  };

  const handleImageUpload = (url) => {
    setFormData({ ...formData, image_url: url });
  };

  const getEventTypeLabel = (type) => {
    const found = EVENT_TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  if (loading) {
    return <div className="flex justify-center py-12">加载中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">海报管理</h2>
          <p className="text-amber-600 text-sm">管理首页展示的活动海报</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              添加海报
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPoster ? '编辑海报 / Edit Poster' : '添加海报 / Add Poster'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 提示信息 */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                💡 填写内容后，其他语言将自动翻译显示
              </div>

              {/* 标题 - 单一输入框 */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="text-red-500">*</span> 
                  <span>标题 / Title</span>
                </Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="输入海报标题 / Enter poster title"
                  required
                />
              </div>

              {/* 简介 - 单一输入框 */}
              <div className="space-y-2">
                <Label>简介 / Subtitle</Label>
                <Textarea
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="输入简介 / Enter subtitle"
                  rows={3}
                />
              </div>

              {/* 图片上传 */}
              <div className="space-y-2">
                <Label>海报图片 / Poster Image *</Label>
                <div className="flex items-center gap-4">
                  <FileUploader
                    onUpload={handleImageUpload}
                    accept="image/*"
                    type="image"
                    token={token}
                    label="上传本地图片 / Upload Image"
                  />
                  <span className="text-sm text-gray-500">或</span>
                  <Input
                    placeholder="输入图片URL / Enter image URL"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="flex-1"
                  />
                </div>
                {formData.image_url && (
                  <div className="mt-2 relative w-full aspect-[3/1] rounded-lg overflow-hidden bg-amber-100">
                    <img
                      src={getImageUrl(formData.image_url)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              {/* 活动信息 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>活动类型</Label>
                  <Select
                    value={formData.event_type}
                    onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* 活动时间 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>开始时间</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  />
                  <p className="text-xs text-amber-500">活动开始前显示"即将开始"倒计时</p>
                </div>
                <div className="space-y-2">
                  <Label>结束时间</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  />
                  <p className="text-xs text-amber-500">活动开始后显示"即将结束"倒计时</p>
                </div>
              </div>

              {/* 活动地点 - 单一输入框 */}
              <div className="space-y-2">
                <Label>活动地点 / Event Location</Label>
                <Input
                  value={formData.event_location}
                  onChange={(e) => setFormData({ ...formData, event_location: e.target.value })}
                  placeholder="例如：北京798艺术区 / e.g. Beijing 798 Art Zone"
                />
              </div>

              <div className="space-y-2">
                <Label>跳转链接 / Link URL</Label>
                <Input
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="例如：/exhibitions/xxx 或 https://..."
                />
              </div>

              {/* 显示设置 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>排序顺序 / Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label>激活显示 / Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  取消 / Cancel
                </Button>
                <Button type="submit">
                  {editingPoster ? '保存 / Save' : '创建 / Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posters Grid */}
      {posters.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Image className="w-12 h-12 mx-auto text-amber-400 mb-4" />
            <p className="text-amber-600">暂无海报，点击上方按钮添加</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {posters.map((poster) => (
            <Card key={poster.id} className={!poster.is_active ? 'opacity-60' : ''}>
              <div className="relative aspect-[3/1] overflow-hidden rounded-t-lg">
                <img
                  src={getImageUrl(poster.image_url)}
                  alt={poster.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2 flex gap-2">
                  <Badge variant={poster.is_active ? 'default' : 'secondary'}>
                    {poster.is_active ? '已激活' : '已隐藏'}
                  </Badge>
                  <Badge variant="outline" className="bg-white/80">
                    {getEventTypeLabel(poster.event_type)}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{poster.title}</h3>
                    {poster.subtitle && (
                      <p className="text-sm text-amber-600 truncate">{poster.subtitle}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-amber-500">
                      {(poster.start_time || poster.end_time) && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {poster.start_time && new Date(poster.start_time).toLocaleDateString('zh-CN')}
                          {poster.start_time && poster.end_time && ' - '}
                          {poster.end_time && new Date(poster.end_time).toLocaleDateString('zh-CN')}
                        </span>
                      )}
                      {poster.event_location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {poster.event_location}
                        </span>
                      )}
                      {poster.link_url && (
                        <span className="flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          有链接
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(poster)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(poster.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPosters;
